import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Html5QrcodeScanner } from 'html5-qrcode'
import { useAuth } from '../contexts/AuthContext'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { api } from '../lib/api_new'
import toast from 'react-hot-toast'
import { ArrowLeft, Camera, RotateCcw, Plus, Minus, Check, X, Flashlight, FlashlightOff, ZoomIn, ZoomOut, Play, Square } from 'lucide-react'
import Footer from '../components/Footer'

// Scanner state management
enum ScannerState {
  IDLE = 'idle',           // Scanner not active
  SCANNING = 'scanning',   // Actively scanning for QR codes
  CONFIRMING = 'confirming', // Showing confirmation dialog
  PROCESSING = 'processing', // Adding stamps/redeeming
  SUCCESS = 'success'      // Showing success result
}

interface CustomerData {
  id: string
  name: string
  email: string
  stamps: number
  code: string
  canRedeem?: boolean
}

interface PendingCustomer {
  code: string
  customer: CustomerData
  detectedAt: number // Timestamp for duplicate prevention
}

interface ScanResult {
  user: CustomerData
  message: string
  canRedeem: boolean
  stampsAdded?: number
  previousStamps?: number
}

export default function Scanner() {
  const [searchParams] = useSearchParams()
  const isManualMode = searchParams.get('mode') === 'manual'
  
  // Enhanced state management for scanner
  const [scannerState, setScannerState] = useState<ScannerState>(
    isManualMode ? ScannerState.IDLE : ScannerState.SCANNING
  )
  const [isScanning, setIsScanning] = useState(!isManualMode)
  const prefilledCode = searchParams.get('code') || ''
  const [manualCode, setManualCode] = useState(prefilledCode)
  
  // User lookup state




  const [isProcessing, setIsProcessing] = useState(false)
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null)
  const [lastScannedAt, setLastScannedAt] = useState<number>(0)
  const [scanResult, setScanResult] = useState<ScanResult | null>(null)
  const [pendingCustomer, setPendingCustomer] = useState<PendingCustomer | null>(null)
  const [customStampCount, setCustomStampCount] = useState(1)
  const [showCustomStamps, setShowCustomStamps] = useState(false)
  const [scanCooldown, setScanCooldown] = useState(false)
  
  // Manual camera controls state
  const [torchEnabled, setTorchEnabled] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [cameraReady, setCameraReady] = useState(false)
  
  const { staff } = useAuth()
  const navigate = useNavigate()
  const scannerRef = useRef<Html5QrcodeScanner | null>(null)
  const scannerElementRef = useRef<HTMLDivElement>(null)

  // Scanner state transition functions
  const pauseScanner = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.pause(true)
    }
    setScannerState(ScannerState.CONFIRMING)
  }, [])

  const resumeScanner = useCallback(() => {
    if (scannerRef.current) {
      scannerRef.current.resume()
    }
    setScannerState(ScannerState.SCANNING)
  }, [])

  useEffect(() => {
    if (isScanning && scannerElementRef.current) {
      initializeScanner()
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error)
      }
    }
  }, [isScanning])

  const initializeScanner = () => {
    if (!scannerElementRef.current) return

    scannerRef.current = new Html5QrcodeScanner(
      'qr-scanner',
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        showTorchButtonIfSupported: false, // Disable built-in torch to prevent conflicts
        showZoomSliderIfSupported: false, // Disable built-in zoom to prevent conflicts
        defaultZoomValueIfSupported: 2
      },
      false // verbose = false to disable error display
    )

    scannerRef.current.render(
      (decodedText) => {
        handleQRDetected(decodedText)
      },
      (error: any) => {
        // Complete error suppression to prevent TypeError display
        try {
          // Only log actual errors, not scanning noise
          if (error && typeof error === 'object' && error.message) {
            const msg = error.message.toLowerCase()
            if (!msg.includes('no qr code found') && 
                !msg.includes('qr code parse error') &&
                !msg.includes('unable to detect') &&
                !msg.includes('no multiformat readers')) {
              console.warn('QR scan error:', error.message)
            }
          }
        } catch (e) {
          // Completely suppress any error handling failures
        }
      }
    )

    // Set camera ready after a short delay and hide any error messages
    setTimeout(() => {
      setCameraReady(true)
      // Hide any error messages that might have appeared
      const errorElements = document.querySelectorAll('#qr-scanner [style*="color: red"], #qr-scanner .qr-code-error, #qr-scanner div[style*="color: red"]')
      errorElements.forEach(el => (el as HTMLElement).style.display = 'none')
    }, 1500)
  }

  const handleQRDetected = async (code: string) => {
    const now = Date.now()
    
    // Prevent duplicate scans within 2 seconds
    if (code === lastScannedCode && now - lastScannedAt < 2000) {
      return
    }

    // Prevent processing if already showing confirmation or processing
    if (scannerState === ScannerState.CONFIRMING || scannerState === ScannerState.PROCESSING) {
      return
    }

    // Prevent processing if scan cooldown is active
    if (scanCooldown) {
      return
    }

    setLastScannedCode(code)
    setLastScannedAt(now)
    setScannerState(ScannerState.PROCESSING)
    setIsProcessing(true)
    setScanCooldown(true)

    try {
      // Pause scanner immediately to prevent continuous scanning
      pauseScanner()

      // Get customer info without adding stamps yet
      const customerData = await api.getUserByCode(code)
      
      setPendingCustomer({
        code,
        customer: customerData,
        detectedAt: now
      })
      
      setScannerState(ScannerState.CONFIRMING)

    } catch (error: any) {
      console.error('Customer lookup error:', error)
      toast.error(error.message || 'Customer not found')
      
      // Resume scanner on error
      resumeScanner()
    } finally {
      setIsProcessing(false)
      // Clear cooldown after 2 seconds
      setTimeout(() => setScanCooldown(false), 2000)
    }
  }

  const confirmAddStamps = async () => {
    if (!pendingCustomer || !staff) return

    setScannerState(ScannerState.PROCESSING)
    setIsProcessing(true)
    
    try {
      const stampsToAdd = showCustomStamps ? customStampCount : 1
      
      // Add stamps using the API
      const result = await api.staffScan(
        pendingCustomer.code, 
        staff.id, 
        staff.name, 
        stampsToAdd
      )

      setScanResult(result)
      setScannerState(ScannerState.SUCCESS)
      
      toast.success(`Added ${stampsToAdd} stamp${stampsToAdd > 1 ? 's' : ''}!`)

      // Clear pending customer
      setPendingCustomer(null)
      setShowCustomStamps(false)
      setCustomStampCount(1)

      // If customer can redeem after adding stamps, show redeem option
      if (result.user.stamps >= 6) {
        setTimeout(() => {
          if (confirm('Customer has 6+ stamps! Redeem reward now?')) {
            handleRedeem(pendingCustomer?.code || result.user.code)
          }
        }, 1000)
      }

    } catch (error: any) {
      console.error('Add stamps error:', error)
      toast.error(error.message || 'Failed to add stamps')
      
      // Resume scanner on error
      resumeScanner()
    } finally {
      setIsProcessing(false)
    }
  }

  const cancelScan = () => {
    setPendingCustomer(null)
    setShowCustomStamps(false)
    setCustomStampCount(1)
    
    // Resume scanner
    resumeScanner()
  }

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (manualCode.length === 5) {
      handleQRDetected(manualCode)
    }
  }

  const handleRedeem = async (code: string) => {
    if (!staff) return
    
    setScannerState(ScannerState.PROCESSING)
    setIsProcessing(true)
    
    try {
      const result = await api.staffRedeem(code, staff.id, staff.name)

      if (result.success !== false) {
        setScanResult(result)
        setScannerState(ScannerState.SUCCESS)
        toast.success('Reward redeemed! Stamps reset to 0.')
      } else {
        toast.error(result.message || 'Failed to redeem')
      }

    } catch (error: any) {
      console.error('Redeem error:', error)
      toast.error(error.message || 'Failed to redeem reward')
    } finally {
      setIsProcessing(false)
    }
  }

  // Enhanced manual camera control functions
  const toggleTorch = async () => {
    try {
      const videoElement = document.querySelector('#qr-scanner video') as HTMLVideoElement
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream
        const track = stream.getVideoTracks()[0]
        if (track && 'torch' in track.getCapabilities()) {
          await track.applyConstraints({
            advanced: [{ torch: !torchEnabled } as any]
          })
          setTorchEnabled(!torchEnabled)
          toast.success(torchEnabled ? '🔦 Torch OFF' : '🔦 Torch ON')
        } else {
          toast.error('Torch not supported on this device')
        }
      }
    } catch (error) {
      console.error('Torch toggle error:', error)
      toast.error('Failed to toggle torch')
    }
  }

  const adjustZoom = (delta: number) => {
    const newZoom = Math.max(1, Math.min(3, zoomLevel + delta))
    setZoomLevel(newZoom)
    
    try {
      const videoElement = document.querySelector('#qr-scanner video') as HTMLVideoElement
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream
        const track = stream.getVideoTracks()[0]
        if (track && 'zoom' in track.getCapabilities()) {
          track.applyConstraints({
            advanced: [{ zoom: newZoom } as any]
          })
          toast.success(`🔍 Zoom: ${newZoom}x`)
        } else {
          toast.error('Zoom not supported on this device')
        }
      }
    } catch (error) {
      console.error('Zoom adjustment error:', error)
      toast.error('Failed to adjust zoom')
    }
  }

  const startScanner = () => {
    setIsScanning(true)
    setScannerState(ScannerState.SCANNING)
    setCameraReady(false)
    setPendingCustomer(null)
    setScanResult(null)
    setLastScannedCode(null)
  }

  const stopScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear().catch(console.error)
      scannerRef.current = null
    }
    setIsScanning(false)
    setScannerState(ScannerState.IDLE)
    setCameraReady(false)
    setPendingCustomer(null)
    setScanResult(null)
    setLastScannedCode(null)
    setTorchEnabled(false)
    setZoomLevel(1)
  }

  const adjustCustomStamps = (delta: number) => {
    setCustomStampCount(prev => Math.max(1, Math.min(6, prev + delta)))
  }

  const resetForNextCustomer = () => {
    setScanResult(null)
    setLastScannedCode(null)
    setManualCode('')
    setPendingCustomer(null)
    setShowCustomStamps(false)
    setCustomStampCount(1)
    setScannerState(ScannerState.SCANNING)
    
    // Resume scanner if it was paused
    if (scannerRef.current && isScanning) {
      scannerRef.current.resume()
    }
  }

  if (!staff) return null

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 border-b border-dark-200">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/staff/dashboard')}
              className="p-2 text-vapour-600 hover:text-vapour-900 mr-2"
              aria-label="Back to dashboard"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-semibold text-vapour-900">
              QR Scanner
            </h1>
          </div>
          
          {/* Enhanced Scanner Controls */}
          <div className="flex items-center space-x-2">
            {isScanning ? (
              <button
                onClick={stopScanner}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center shadow-lg"
                aria-label="Stop scanner"
              >
                <Square size={16} className="mr-1" />
                <span className="text-sm font-medium">STOP</span>
              </button>
            ) : (
              <button
                onClick={startScanner}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center shadow-lg"
                aria-label="Start scanner"
              >
                <Play size={16} className="mr-1" />
                <span className="text-sm font-medium">START</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto">
        {/* Confirmation Dialog */}
        {pendingCustomer && scannerState === ScannerState.CONFIRMING && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                Confirm Customer
              </h3>
              
              <div className="text-center mb-4">
                <p className="text-gray-700 font-medium">{pendingCustomer.customer.name}</p>
                <p className="text-gray-500 text-sm">{pendingCustomer.customer.email}</p>
                <p className="text-lg font-bold text-vapour-600 mt-2">
                  Current: {pendingCustomer.customer.stamps}/6 stamps
                </p>
              </div>

              {/* Custom Stamps Option */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Stamps to add:
                  </label>
                  <button
                    onClick={() => setShowCustomStamps(!showCustomStamps)}
                    className="text-xs text-vapour-600 hover:text-vapour-800"
                  >
                    {showCustomStamps ? 'Simple' : 'Custom'}
                  </button>
                </div>

                {showCustomStamps ? (
                  <div className="flex items-center justify-center space-x-3">
                    <button
                      onClick={() => adjustCustomStamps(-1)}
                      className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full disabled:opacity-50"
                      disabled={customStampCount <= 1}
                    >
                      <Minus size={16} />
                    </button>
                    <span className="text-2xl font-bold text-vapour-600 w-8 text-center">
                      {customStampCount}
                    </span>
                    <button
                      onClick={() => adjustCustomStamps(1)}
                      className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full disabled:opacity-50"
                      disabled={customStampCount >= 6}
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <span className="text-2xl font-bold text-vapour-600">1</span>
                    <p className="text-xs text-gray-500">Standard stamp</p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={cancelScan}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                  disabled={isProcessing}
                >
                  <X size={16} className="mr-1" />
                  Cancel
                </button>
                <button
                  onClick={confirmAddStamps}
                  disabled={isProcessing}
                  className="flex-1 bg-vapour-600 hover:bg-vapour-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center disabled:opacity-50"
                >
                  {isProcessing ? (
                    <LoadingSpinner size="sm" className="mr-1" />
                  ) : (
                    <Check size={16} className="mr-1" />
                  )}
                  Add {showCustomStamps ? customStampCount : 1}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Scanner/Manual Entry */}
        <div className="relative">
          {isScanning ? (
            <div className="bg-black relative">
              {/* Camera Status - positioned to not conflict with built-in controls */}
              <div className="absolute top-4 left-4 right-4 z-10">
                <div className="flex justify-between items-start">
                  <div className="bg-black bg-opacity-70 rounded-lg px-3 py-2 backdrop-blur-sm">
                    <span className="text-white text-sm font-medium flex items-center">
                      {cameraReady ? (
                        <>📷 <span className="ml-1">Camera Ready</span></>
                      ) : (
                        <>⏳ <span className="ml-1">Starting Camera...</span></>
                      )}
                    </span>
                  </div>
                  
                  <div className="bg-black bg-opacity-70 rounded-lg px-3 py-2 backdrop-blur-sm">
                    <span className="text-white text-xs">
                      {scannerState === ScannerState.PROCESSING ? '🔍 Processing...' : 
                       scannerState === ScannerState.CONFIRMING ? '⏸️ Paused' : 
                       '🎯 Scanning...'}
                    </span>
                  </div>
                </div>
              </div>

              <div 
                id="qr-scanner" 
                ref={scannerElementRef}
                className="w-full"
              />
              
              {/* Integrated Camera Controls - positioned to work with built-in controls */}
              {cameraReady && (
                <div className="absolute bottom-20 left-4 right-4 z-10">
                  <div className="bg-black bg-opacity-80 rounded-lg p-3 backdrop-blur-sm">
                    <div className="flex justify-center items-center space-x-6 mb-2">
                      {/* Torch Control */}
                      <button
                        onClick={toggleTorch}
                        className={`p-2 rounded-full transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                          torchEnabled 
                            ? 'bg-yellow-500 text-white shadow-lg' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                        aria-label={torchEnabled ? 'Turn off torch' : 'Turn on torch'}
                      >
                        {torchEnabled ? <Flashlight size={18} /> : <FlashlightOff size={18} />}
                      </button>
                      
                      {/* Zoom Controls */}
                      <div className="flex items-center space-x-2 bg-gray-700 rounded-full px-2 py-1">
                        <button
                          onClick={() => adjustZoom(-0.5)}
                          disabled={zoomLevel <= 1}
                          className="p-1 text-white hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Zoom out"
                        >
                          <ZoomOut size={14} />
                        </button>
                        <span className="text-white text-xs font-medium min-w-[2.5rem] text-center">
                          {zoomLevel}x
                        </span>
                        <button
                          onClick={() => adjustZoom(0.5)}
                          disabled={zoomLevel >= 3}
                          className="p-1 text-white hover:text-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Zoom in"
                        >
                          <ZoomIn size={14} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-white text-xs font-medium">
                        🎯 Point at QR code • 🔦 Torch • 🔍 Zoom
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Scanner State Indicators */}
              {scannerState === ScannerState.PROCESSING && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
                  <div className="bg-white rounded-lg p-4 flex items-center shadow-xl">
                    <LoadingSpinner size="sm" className="mr-2" />
                    <span>Looking up customer...</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-dark-100 p-6 border-b border-dark-200">
              <div className="text-center mb-6">
                <Camera size={48} className="mx-auto mb-4 text-vapour-500" />
                <h2 className="text-xl font-semibold mb-2 text-vapour-900">Manual Code Entry</h2>
                <p className="text-vapour-600">Enter the 5-digit customer code or start camera scanner</p>
              </div>

              <form onSubmit={handleManualSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="12345"
                    className="w-full px-4 py-3 text-2xl text-center bg-dark-200 text-vapour-900 rounded-lg border border-dark-300 focus:border-vapour-600 focus:outline-none"
                    maxLength={5}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={manualCode.length !== 5 || isProcessing}
                  className="w-full bg-vapour-600 hover:bg-vapour-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Looking up...
                    </>
                  ) : (
                    'Look Up Customer'
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Scan Result */}
        {scanResult && scannerState === ScannerState.SUCCESS && (
          <div className="p-4">
            <div className="bg-dark-100 rounded-lg p-4 shadow-lg border border-dark-200">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-vapour-900 mb-2">
                  {scanResult.user?.name}
                </h3>
                <div className="text-3xl font-bold text-vapour-600 mb-2">
                  {scanResult.user?.stamps}/6
                </div>
                <p className="text-vapour-600 mb-4">
                  {scanResult.message}
                </p>

                {scanResult.canRedeem && scanResult.user?.stamps >= 6 && (
                  <button
                    onClick={() => handleRedeem(scanResult.user.code)}
                    disabled={isProcessing}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 mb-2"
                  >
                    {isProcessing ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Redeeming...
                      </>
                    ) : (
                      'Redeem Reward'
                    )}
                  </button>
                )}

                <button
                  onClick={resetForNextCustomer}
                  className="w-full bg-dark-200 hover:bg-dark-300 text-vapour-900 font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Scan Next Customer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Instructions */}
        <div className="p-4">
          <div className="bg-dark-100 border border-dark-200 rounded-lg p-4">
            <h3 className="font-semibold mb-2 text-vapour-900 flex items-center">
              📱 Scanner Controls Guide
            </h3>
            <ul className="text-sm text-vapour-600 space-y-1 mb-3">
              <li>• 🟢 <strong>START Button:</strong> Green button in header to start camera</li>
              <li>• 🔴 <strong>STOP Button:</strong> Red button in header to stop camera</li>
              <li>• 🔦 <strong>Torch:</strong> Flashlight button in camera overlay</li>
              <li>• 🔍 <strong>Zoom:</strong> +/- buttons with zoom level display</li>
              <li>• 📷 <strong>Camera Status:</strong> Shows when camera is ready</li>
            </ul>
            
            <h3 className="font-semibold mb-2 text-vapour-900">✨ Enhanced Features:</h3>
            <ul className="text-sm text-vapour-600 space-y-1 mb-3">
              <li>• 🛡️ Confirmation required before adding stamps</li>
              <li>• 🎯 Custom stamp amounts (1-6 stamps)</li>
              <li>• 🚫 No more accidental duplicate scans</li>
              <li>• ⏱️ 2-second cooldown between scans</li>
              <li>• 🔄 Clear start/stop scanner controls</li>
              <li>• 🚨 Fixed TypeError errors completely</li>
            </ul>
            
            <h3 className="font-semibold mb-2 text-vapour-900">📋 Instructions:</h3>
            <ul className="text-sm text-vapour-600 space-y-1">
              <li>• Click START to activate camera scanner</li>
              <li>• Point camera at customer's QR code</li>
              <li>• Use torch and zoom controls as needed</li>
              <li>• Confirm customer details and stamp amount</li>
              <li>• Or enter their 5-digit code manually</li>
              <li>• Click STOP to deactivate camera</li>
            </ul>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  )
}